import { toast } from 'react-toastify'
import {
  FETCH_CAMPAIGNS_START, FETCH_CAMPAIGNS_SUCCESS, FETCH_CAMPAIGNS_ERROR,
  FETCH_CAMPAIGN_DETAIL_START, FETCH_CAMPAIGN_DETAIL_SUCCESS, FETCH_CAMPAIGN_DETAIL_ERROR,
  CREATE_CAMPAIGN_START, CREATE_CAMPAIGN_SUCCESS, CREATE_CAMPAIGN_ERROR,
  UPDATE_CAMPAIGN_START, UPDATE_CAMPAIGN_SUCCESS, UPDATE_CAMPAIGN_ERROR,
  CANCEL_CAMPAIGN_START, CANCEL_CAMPAIGN_SUCCESS, CANCEL_CAMPAIGN_ERROR,
  CANCEL_CREATORS_START, CANCEL_CREATORS_SUCCESS, CANCEL_CREATORS_ERROR,
  DELETE_CAMPAIGN_START, DELETE_CAMPAIGN_SUCCESS, DELETE_CAMPAIGN_ERROR,
  FETCH_PROPOSALS_START, FETCH_PROPOSALS_SUCCESS, FETCH_PROPOSALS_ERROR,
  SUBMIT_PROPOSAL_START, SUBMIT_PROPOSAL_SUCCESS, SUBMIT_PROPOSAL_ERROR,
  ACCEPT_INVITE_START, ACCEPT_INVITE_SUCCESS, ACCEPT_INVITE_ERROR,
  UPDATE_PROPOSAL_START, UPDATE_PROPOSAL_SUCCESS, UPDATE_PROPOSAL_ERROR,
  FETCH_MY_PROPOSALS_START, FETCH_MY_PROPOSALS_SUCCESS, FETCH_MY_PROPOSALS_ERROR,
  FETCH_MY_ASSIGNMENTS_START, FETCH_MY_ASSIGNMENTS_SUCCESS, FETCH_MY_ASSIGNMENTS_ERROR,
  SUBMIT_MY_ASSIGNMENT_START, SUBMIT_MY_ASSIGNMENT_SUCCESS, SUBMIT_MY_ASSIGNMENT_ERROR,
  FETCH_DAILY_BID_USAGE_START,FETCH_DAILY_BID_USAGE_SUCCESS,FETCH_DAILY_BID_USAGE_ERROR,
} from '../actionTypes'
import {
  fetchCampaignsApi, fetchCampaignDetailApi, createCampaignApi, updateCampaignApi,
  cancelCampaignApi, cancelCreatorsApi, deleteCampaignApi, fetchProposalsApi, submitProposalApi, acceptInviteApi, updateProposalApi, fetchMyProposalsApi,
  fetchMyAssignmentsApi, submitMyAssignmentApi,fetchDailyBidUsageApi,
} from '../../Api/Campaign/campaignsApi'

const parseError = (error) =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message || "Something went wrong"

export const fetchCampaignsAction = (params = {}) => async (dispatch) => {
  dispatch({ type: FETCH_CAMPAIGNS_START })
  try {
    const res = await fetchCampaignsApi(params)
    dispatch({ type: FETCH_CAMPAIGNS_SUCCESS, payload: res.data })
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: FETCH_CAMPAIGNS_ERROR, payload: msg })
    // Agency-managed creators are blocked from campaign discovery by the backend
    // (403). That's an expected restriction for them, not an actionable error —
    // so don't spam them with a toast.
    const isDiscoveryRestricted =
      error?.response?.status === 403 && /campaign discovery/i.test(msg)
    if (!isDiscoveryRestricted) toast.error(msg)
  }
}

export const fetchCampaignDetailAction = (id) => async (dispatch) => {
  dispatch({ type: FETCH_CAMPAIGN_DETAIL_START })
  try {
    const res = await fetchCampaignDetailApi(id)
    dispatch({ type: FETCH_CAMPAIGN_DETAIL_SUCCESS, payload: res.data })
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: FETCH_CAMPAIGN_DETAIL_ERROR, payload: msg })
    toast.error(msg)
  }
}

export const createCampaignAction = (data, onSuccess, onError) => async (dispatch) => {
  dispatch({ type: CREATE_CAMPAIGN_START })
  try {
    const res = await createCampaignApi(data)
    dispatch({ type: CREATE_CAMPAIGN_SUCCESS, payload: res.data })
    const isDraft = data.status === 'draft'
    toast.success(isDraft ? 'Campaign saved as draft!' : 'Campaign created and published!')
    onSuccess && onSuccess(res.data.campaign)
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: CREATE_CAMPAIGN_ERROR, payload: msg })
    if (onError) {
      onError({ status: error?.response?.status, data: error?.response?.data, message: msg, error })
    } else {
      toast.error(msg)
    }
  }
}

export const updateCampaignAction = (id, data, onSuccess) => async (dispatch) => {
  dispatch({ type: UPDATE_CAMPAIGN_START })
  try {
    const res = await updateCampaignApi(id, data)
    dispatch({ type: UPDATE_CAMPAIGN_SUCCESS, payload: res.data })
    toast.success('Campaign updated!')
    onSuccess && onSuccess(res.data.campaign)
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: UPDATE_CAMPAIGN_ERROR, payload: msg })
    toast.error(msg)
  }
}

export const cancelCampaignAction = (id, onSuccess) => async (dispatch) => {
  dispatch({ type: CANCEL_CAMPAIGN_START })
  try {
    const res = await cancelCampaignApi(id)
    const requiresReview = res.data?.requires_review ?? false
    dispatch({ type: CANCEL_CAMPAIGN_SUCCESS, payload: { id, campaign: res.data?.campaign } })
    toast.success(res.data?.message || 'Campaign cancelled')
    onSuccess && onSuccess(requiresReview)
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: CANCEL_CAMPAIGN_ERROR, payload: msg })
    toast.error(msg)
  }
}

export const cancelCreatorsAction = (campaignId, proposalIds, onSuccess) => async (dispatch) => {
  dispatch({ type: CANCEL_CREATORS_START })
  try {
    const res = await cancelCreatorsApi(campaignId, proposalIds)
    dispatch({ type: CANCEL_CREATORS_SUCCESS, payload: { id: campaignId, campaign: res.data?.campaign } })
    toast.success(res.data?.message || 'Creators removed')
    if (res.data?.blocked?.length > 0) {
      res.data.blocked.forEach((b) => toast.warning(b.reason, { autoClose: 6000 }))
    }
    onSuccess && onSuccess(res.data)
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: CANCEL_CREATORS_ERROR, payload: msg })
    toast.error(msg)
  }
}

export const deleteCampaignAction = (id, onSuccess) => async (dispatch) => {
  dispatch({ type: DELETE_CAMPAIGN_START })
  try {
    await deleteCampaignApi(id)
    dispatch({ type: DELETE_CAMPAIGN_SUCCESS, payload: { id } })
    toast.success('Campaign deleted')
    onSuccess && onSuccess()
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: DELETE_CAMPAIGN_ERROR, payload: msg })
    toast.error(msg)
  }
}

export const fetchProposalsAction = (campaignId) => async (dispatch) => {
  dispatch({ type: FETCH_PROPOSALS_START })
  try {
    const res = await fetchProposalsApi(campaignId)
    dispatch({ type: FETCH_PROPOSALS_SUCCESS, payload: res.data })
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: FETCH_PROPOSALS_ERROR, payload: msg })
    toast.error(msg)
  }
}

export const submitProposalAction = (campaignId, data, onSuccess) => async (dispatch) => {
  dispatch({ type: SUBMIT_PROPOSAL_START })
  try {
    const res = await submitProposalApi(campaignId, data)
    dispatch({ type: SUBMIT_PROPOSAL_SUCCESS, payload: res.data })
    toast.success('Proposal submitted!')
    onSuccess && onSuccess(res.data.proposal)
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: SUBMIT_PROPOSAL_ERROR, payload: msg })
    toast.error(msg)
  }
}

export const acceptInviteAction = (campaignId, onSuccess) => async (dispatch) => {
  dispatch({ type: ACCEPT_INVITE_START })
  try {
    const res = await acceptInviteApi(campaignId)
    dispatch({ type: ACCEPT_INVITE_SUCCESS, payload: res.data })
    toast.success('Invite accepted! Waiting for the brand to release payment.')
    onSuccess && onSuccess(res.data.proposal)
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: ACCEPT_INVITE_ERROR, payload: msg })
    toast.error(msg)
  }
}

export const updateProposalAction = (proposalId, data, onSuccess) => async (dispatch) => {
  dispatch({ type: UPDATE_PROPOSAL_START })
  try {
    const res = await updateProposalApi(proposalId, data)
    dispatch({ type: UPDATE_PROPOSAL_SUCCESS, payload: res.data })
    const { status } = data
    const msg = status === 'accepted' ? 'Proposal accepted!' : status === 'rejected' ? 'Proposal rejected' : status === 'abandoned' ? 'You\'ve stopped work on this campaign. The brand has been notified.' : 'Proposal withdrawn'
    toast.success(msg)
    onSuccess && onSuccess(res.data.proposal)
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: UPDATE_PROPOSAL_ERROR, payload: msg })
    toast.error(msg)
  }
}

export const fetchMyProposalsAction = () => async (dispatch) => {
  dispatch({ type: FETCH_MY_PROPOSALS_START })
  try {
    const res = await fetchMyProposalsApi()
    dispatch({ type: FETCH_MY_PROPOSALS_SUCCESS, payload: res.data })
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: FETCH_MY_PROPOSALS_ERROR, payload: msg })
    toast.error(msg)
  }
}

export const fetchMyAssignmentsAction = () => async (dispatch) => {
  dispatch({ type: FETCH_MY_ASSIGNMENTS_START })
  try {
    const res = await fetchMyAssignmentsApi()
    dispatch({ type: FETCH_MY_ASSIGNMENTS_SUCCESS, payload: res.data })
  } catch (error) {
    dispatch({ type: FETCH_MY_ASSIGNMENTS_ERROR, payload: parseError(error) })
  }
}

export const submitMyAssignmentAction = (id, data, files, onSuccess) => async (dispatch) => {
  dispatch({ type: SUBMIT_MY_ASSIGNMENT_START })
  try {
    const res = await submitMyAssignmentApi(id, data, files)
    dispatch({ type: SUBMIT_MY_ASSIGNMENT_SUCCESS, payload: res.data })
    toast.success('Work submitted! Your agency will review it.')
    onSuccess && onSuccess(res.data.assignment)
  } catch (error) {
    const msg = parseError(error)
    dispatch({ type: SUBMIT_MY_ASSIGNMENT_ERROR, payload: msg })
    toast.error(msg)
  }
}
export const fetchDailyBidUsageAction = () => async (dispatch) => {
  dispatch({ type: FETCH_DAILY_BID_USAGE_START })

  try {
    const res = await fetchDailyBidUsageApi()
    const usage = res.data?.data ?? res.data

    dispatch({
      type: FETCH_DAILY_BID_USAGE_SUCCESS,
      payload: usage,
    })

    return usage
  } catch (error) {
    const msg = parseError(error)

    dispatch({
      type: FETCH_DAILY_BID_USAGE_ERROR,
      payload: msg,
    })

    return null
  }
}
